import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function ProductReviewForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [requiresAccountLink, setRequiresAccountLink] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [productRatings, setProductRatings] = useState({
    quality: 0,
    valueForMoney: 0,
    asDescribed: 0,
  });

  const selectedProduct = useMemo(
    () => products.find((p) => p.productId === selectedProductId),
    [products, selectedProductId],
  );

  useEffect(() => {
    if (!token) {
      setError(
        "Invalid product review link. Please use the link in your email.",
      );
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/reviews/product-review-by-token?token=${token}`,
        );

        const orderData = response.data.order;
        const productData = response.data.products || [];

        setOrder(orderData);
        setProducts(productData);
        setRequiresAccountLink(response.data.requiresAccountLink === true);

        const firstUnreviewed = productData.find((p) => !p.alreadyReviewed);
        if (firstUnreviewed) {
          setSelectedProductId(firstUnreviewed.productId);
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Unable to load product review details.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setReviewText("");
    setProductRatings({ quality: 0, valueForMoney: 0, asDescribed: 0 });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!selectedProduct) {
      setError("Please select a product to review.");
      return;
    }

    if (rating === 0) {
      setError("Please select an overall rating.");
      return;
    }

    // U2: only require a non-empty review; the old 10-char minimum could
    // block customers who wrote a short but real review.
    if (!reviewText.trim()) {
      setError("Please add a short review before submitting.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(
        `${API_BASE_URL}/api/reviews/submit-product-with-token`,
        {
          reviewToken: token,
          orderId: order?._id,
          productId: selectedProduct.productId,
          shopId: selectedProduct.shopId,
          rating,
          reviewText: reviewText.trim(),
          productRatings,
          photos: [],
        },
      );

      setProducts((prev) =>
        prev.map((product) =>
          product.productId === selectedProduct.productId
            ? { ...product, alreadyReviewed: true }
            : product,
        ),
      );

      setSuccessMessage("Thank you. Your product feedback has been submitted.");
      resetForm();

      const nextUnreviewed = products.find(
        (product) =>
          product.productId !== selectedProduct.productId &&
          !product.alreadyReviewed,
      );
      if (nextUnreviewed) {
        setSelectedProductId(nextUnreviewed.productId);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          background: "#ffffff",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 6px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
            color: "#ffffff",
            padding: "28px 24px",
            textAlign: "center",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "30px", fontWeight: 700 }}>
            Product Feedback
          </h1>
          <p style={{ margin: "10px 0 0", opacity: 0.9 }}>
            Share your experience with the products from your order
          </p>
        </div>

        <div style={{ padding: "24px" }}>
          {error ? (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 14px",
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                color: "#991b1b",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          ) : null}

          {successMessage ? (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px 14px",
                background: "#dcfce7",
                border: "1px solid #86efac",
                borderRadius: "8px",
                color: "#166534",
                fontSize: "14px",
              }}
            >
              {successMessage}
            </div>
          ) : null}

          {requiresAccountLink ? (
            <div
              style={{
                marginBottom: "20px",
                padding: "12px 14px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: "8px",
                color: "#9a3412",
                fontSize: "14px",
              }}
            >
              This order is not linked to a customer account yet. Create or sign
              in with the order email to submit product feedback.
            </div>
          ) : null}

          {order ? (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "14px",
                marginBottom: "18px",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                Order {order.orderNumber}
              </div>
              <div style={{ fontSize: "14px", color: "#475569" }}>
                Date: {new Date(order.orderDate).toLocaleDateString("en-US")}
              </div>
              <div style={{ fontSize: "14px", color: "#475569" }}>
                Total: ${(Number(order.totalAmount) || 0).toFixed(2)}
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <label
              style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}
            >
              Select Product
            </label>
            <select
              value={selectedProductId}
              onChange={(event) => setSelectedProductId(event.target.value)}
              style={{
                width: "100%",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "10px 12px",
                marginBottom: "16px",
              }}
            >
              <option value="">Choose product</option>
              {products.map((product) => (
                <option
                  key={`${product.shopId}-${product.productId}`}
                  value={product.productId}
                >
                  {product.productName} ({product.shopName})
                  {product.alreadyReviewed ? " - already reviewed" : ""}
                </option>
              ))}
            </select>

            <label
              style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}
            >
              Overall Rating
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: "28px",
                    cursor: "pointer",
                    color:
                      star <= (hoverRating || rating) ? "#f59e0b" : "#cbd5e1",
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  *
                </button>
              ))}
            </div>

            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "14px",
                marginBottom: "16px",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: "10px" }}>
                Detailed Ratings (optional)
              </div>
              {[
                { key: "quality", label: "Quality" },
                { key: "valueForMoney", label: "Value for Money" },
                { key: "asDescribed", label: "As Described" },
              ].map((item) => (
                <div key={item.key} style={{ marginBottom: "10px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#475569",
                      marginBottom: "6px",
                    }}
                  >
                    {item.label}
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setProductRatings((prev) => ({
                            ...prev,
                            [item.key]: value,
                          }))
                        }
                        style={{
                          border: "none",
                          background: "transparent",
                          fontSize: "20px",
                          cursor: "pointer",
                          color:
                            value <= (productRatings[item.key] || 0)
                              ? "#f59e0b"
                              : "#cbd5e1",
                          padding: 0,
                          lineHeight: 1,
                        }}
                      >
                        *
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <label
              style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}
            >
              Your Review
            </label>
            <textarea
              value={reviewText}
              onChange={(event) => setReviewText(event.target.value)}
              rows={5}
              maxLength={1000}
              placeholder="Tell us about your product experience"
              style={{
                width: "100%",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "10px 12px",
                marginBottom: "16px",
                resize: "vertical",
              }}
            />

            <button
              type="submit"
              disabled={
                submitting ||
                requiresAccountLink ||
                !selectedProduct ||
                selectedProduct.alreadyReviewed
              }
              style={{
                width: "100%",
                border: "none",
                borderRadius: "8px",
                padding: "12px 16px",
                color: "#ffffff",
                background:
                  submitting ||
                  requiresAccountLink ||
                  !selectedProduct ||
                  selectedProduct.alreadyReviewed
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
                fontWeight: 700,
                cursor:
                  submitting ||
                  requiresAccountLink ||
                  !selectedProduct ||
                  selectedProduct.alreadyReviewed
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {selectedProduct?.alreadyReviewed
                ? "Product Already Reviewed"
                : submitting
                  ? "Submitting..."
                  : "Submit Product Feedback"}
            </button>

            <p
              style={{
                margin: "12px 0 0 0",
                fontSize: "11px",
                color: "#9ca3af",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Your feedback will be publicly visible after approval. By submitting
              it you agree to your first name being shown alongside it - your
              surname is never displayed.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProductReviewForm;
